// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { BrandDto } from "@insite/client-framework/Types/ApiModels";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    brand: BrandDto;
    /**
     * If true, the small brand logo will be displayed.
     * If false, the brand name will be displayed instead.
     * Default value: false */
    showLogo?: boolean;
    extendedStyles?: ProductBrandStyles;
}

type Props = OwnProps;

export interface ProductBrandStyles {
    logoImage?: LazyImageProps;
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
    wrapper?: InjectableCss;
}

const nameText: TypographyProps = {
    css: css`
        width: 100%;
        ${wrapInContainerStyles}
    `,
    size: "13px",
    weight: 600,
};

export const productBrandStyles: ProductBrandStyles = {
    nameText,
    nameLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: nameText,
    },
};

// XNG-Change: add wrapper for link, to prevent it from taking full width
const ProductBrand: FC<Props> = ({ brand, showLogo = false, extendedStyles }) => {
    if (!brand) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(productBrandStyles, extendedStyles));

    let brandDisplay;
    if (showLogo && brand.logoSmallImagePath) {
        const altText = brand.logoImageAltText || brand.name;
        brandDisplay = <LazyImage {...styles.logoImage} src={brand.logoSmallImagePath} altText={altText} />;
    } else {
        brandDisplay = brand.detailPagePath ? (
            brand.name
        ) : (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.nameText} data-test-selector="brandName">
                    {brand.name}
                </Typography>
            </StyledWrapper>
        );
    }

    return brand.detailPagePath ? (
        <StyledWrapper {...styles.wrapper}>
            <Link
                aria-label={brand.name}
                {...styles.nameLink}
                href={brand.detailPagePath}
                data-test-selector="brandLink"
            >
                {brandDisplay}
            </Link>
        </StyledWrapper>
    ) : (
        <>{brandDisplay}</>
    );
};

export default ProductBrand;
